import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";

import { Dropzone } from "@/features/project-data/Dropzone";
import { DonesiKonzola } from "@/features/project-data/DonesiKonzola";
import { OpisZahvataInline } from "@/features/project-data/OpisZahvataPanel";
import {
  applyOfficeProfileToDraft,
  useDraftPrefillSink,
} from "@/features/project-data/prefillToDraft";
import { useExtractionStore } from "@/features/project-data/useExtractionStore";
import { useRecognizeDocs } from "@/features/project-data/useRecognizeDocs";
import { useOfficeStore } from "@/features/projects/useOfficeStore";
import { useProjectStore } from "@/features/projects/useProjectStore";
import { Button } from "@/components/ui/button";

/**
 * Korak 1 — DONESI: koš na vrhu + konzola (Lukin feedback 14.07.).
 *
 * The BASKET is a slim strip at the TOP — "istovari sve odjednom", CAD +
 * PDFs, the system routes. The KONZOLA below is the precise path AND the
 * overview: every row is a drop-zone / inline entry with its status and
 * evidence in place (incl. the opis, dictated or typed, inline on its
 * row). No form here: uploads land in persistent state via
 * ``useDraftPrefillSink`` (draft + extraction store), and the Provjeri
 * screen hydrates the form from there. One CTA → Provjeri.
 */
export function DonesiPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const project = useProjectStore((s) =>
    s.projects.find((p) => p.id === projectId),
  );
  const upsert = useProjectStore((s) => s.upsert);
  const setActive = useProjectStore((s) => s.setActive);

  // Off-form prefill sink — the basket and the inline opis write through
  // this into the draft, surviving the hop to the Provjeri route.
  const sink = useDraftPrefillSink(project?.id ?? "");

  // Status/recognition params, derived from the feasibility draft so the
  // basket and the console reason about the same zahvat.
  const opciDioParams = {
    building_type: project?.feasibility?.building_type ?? "obiteljska_kuca",
    building_subtype: project?.feasibility?.building_subtype || null,
    context: project?.feasibility?.context || null,
    work_type: project?.feasibility?.work_type ?? "novogradnja",
  };

  // The top basket's PDF router (untargeted — no expected_type hint).
  const { addPdfFiles, queue, errors } = useRecognizeDocs(
    project?.id ?? "",
    opciDioParams,
  );

  // Prefill receipt: with no form on this screen, the architect needs a
  // visible confirmation that an upload actually read fields into the draft
  // (the fields themselves show on the next step). Count from the same
  // extraction store the form's stamps read from.
  const confidence = useExtractionStore((s) => s.confidence);
  const prefilled = useMemo(
    () =>
      Object.values(confidence).filter(
        (f) => f && f.value != null && f.value !== "",
      ),
    [confidence],
  );
  const prefilledCount = prefilled.length;
  const lowConf = prefilled.filter((f) => (f.confidence ?? 1) < 0.75).length;

  const office = useOfficeStore((s) => s.office);

  useEffect(() => {
    if (project) {
      setActive(project.id);
      // Point the extraction mirror at THIS project before any upload writes.
      useExtractionStore.getState().setActiveProject(project.id);
    }
  }, [project, setActive]);

  // Office profile → draft (fill-empty): ured/projektant set once in
  // Postavke should light up here on every project, not stay a second,
  // disconnected copy of the same data (Lukin nalaz 14.07.).
  useEffect(() => {
    if (project?.id) applyOfficeProfileToDraft(project.id, office);
  }, [project?.id, office]);

  if (!project) {
    return (
      <div className="rounded-lg border border-stone-200 bg-white p-8 text-center">
        <p className="text-stone-500">Projekt nije pronađen.</p>
        <Button className="mt-3" onClick={() => navigate("/")}>
          Natrag na početak
        </Button>
      </div>
    );
  }

  const proceed = () => {
    // Mark Provjeri as reached so the stepper unlocks it, then go.
    if ((project.currentStep ?? 1) < 2) {
      upsert({ ...project, currentStep: 2 });
    }
    navigate(`/project/${project.id}/provjeri`);
  };

  return (
    <>
      <div className="mb-4">
        <h2 className="text-[1.05rem] font-bold text-stone-800">
          Donesi — {project.name}
        </h2>
        <p className="text-[0.88rem] text-stone-500">
          Istovari sve odjednom u koš — mi razvrstamo po recima. Ili povuci
          datoteku ravno na njen redak niže.
        </p>
      </div>

      {/* THE basket — one slim strip at the top for "everything at once"
          (CAD + PDFs). What lands here shows up on its console row below. */}
      <Dropzone
        compact
        projectId={project.id}
        onPrefill={(values, ifc, kind) =>
          sink.current?.applyExtraction(values, ifc, kind)
        }
        onPdfFiles={(files) => void addPdfFiles(files)}
      />

      {queue.length > 0 && (
        <div className="mt-2 flex items-center gap-2 text-[0.82rem] text-stone-600">
          <Loader2 className="h-4 w-4 animate-spin text-ind-600" />
          Prepoznajem: {queue.join(", ")}…
        </div>
      )}

      {/* Opis zahvata — part of the ENTRY zone (Lukin feedback 15.07.):
          one free text fills many fields at once and becomes the opis
          section, so it sits next to the basket, not buried in a row.
          The console's "Opis zahvata" row reports its status below. */}
      <div
        id="opis-editor"
        className="mt-2 scroll-mt-24 rounded-xl border border-stone-200 bg-white px-4 pb-2 pt-2.5 shadow-sm"
      >
        <div className="text-[0.72rem] font-bold uppercase tracking-[0.05em] text-stone-500">
          Ili opiši zahvat riječima{" "}
          <span className="font-normal normal-case text-stone-400">
            — polja se popune sama iz teksta, diktata ili projektnog zadatka
          </span>
        </div>
        <OpisZahvataInline
          key={project.id}
          initialText={project.projectData?.zahvat_opis ?? ""}
          formApi={sink}
          documentUpload
        />
      </div>
      {errors.length > 0 && (
        <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-[0.8rem] text-red-700">
          {errors.map((e, i) => (
            <p key={i}>Greška: {e}</p>
          ))}
        </div>
      )}

      {prefilledCount > 0 && (
        <div className="mt-2 flex items-start gap-2 rounded-md border border-green-200 bg-green-50 px-4 py-2.5 text-[0.85rem] text-green-800">
          <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            <span className="font-semibold">
              Pročitano iz izvora — {prefilledCount}{" "}
              {prefilledCount === 1 ? "polje popunjeno" : "polja popunjeno"}
            </span>
            {lowConf > 0 && (
              <span className="text-green-700">
                {" "}
                ({lowConf} za provjeru)
              </span>
            )}
            . Vrijednosti su spremljene — pregledaš i ispraviš ih na koraku{" "}
            <strong>Provjeri</strong>.
          </div>
        </div>
      )}

      {/* The console — what Mapa 1 expects, as actionable rows: every row
          is one place for input, status and evidence (F1, Ivina skica). */}
      <div className="mt-4">
        <DonesiKonzola projectId={project.id} params={opciDioParams} />
      </div>

      {/* Sticky action bar — one CTA: move to Provjeri. */}
      <div className="sticky bottom-0 z-30 -mx-6 mt-6 border-t border-stone-200 bg-white/95 px-6 py-3 backdrop-blur">
        <div className="flex items-center justify-between gap-4">
          <span className="text-[0.78rem] text-stone-400">
            Učitano se automatski sprema u nacrt projekta.
          </span>
          <Button onClick={proceed}>
            {prefilledCount > 0
              ? `Dalje — provjeri ${prefilledCount} ${prefilledCount === 1 ? "polje" : "polja"}`
              : "Dalje — provjeri podatke"}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </>
  );
}
